import "./style.scss";

import { Collapse } from "@mui/material";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "store/store";

import Channel from "../Channel";
import ChannelDetails from "../ChannelDetails";
import type { IChannel } from "../types";

interface IChannelList {
    /**
     * filtered channels after search or filter by category
     */
    filteredChannels: IChannel[];
    /**
     * open edit modal
     */
    onEditChannel: () => void;
    /**
     * open delete modal
     */
    onDeleteChannel: () => void;
    /**
     * open channel while click on it + set data of the channel in a state
     */
    onOpenChannel: (currentChannel: IChannel) => void;
    /**
     * current opened channel details
     */
    onTargetChannelId: number | null;
    /**
     * Filtered and Paginated Channels
     */
    paginatedChannels: IChannel[];
    /**
     * If user has edit permissions
     */
    hasEditPermission: boolean;
}

const ChannelsList = ({
    filteredChannels,
    onEditChannel,
    onDeleteChannel,
    onOpenChannel,
    onTargetChannelId,
    paginatedChannels,
    hasEditPermission,
}: IChannelList) => {
    const [t] = useTranslation();
    const { channelCompensationGroups, channelsStatus } = useSelector((state: RootState) => state.channels);
    const { getCompanySettings } = useCompanySettings();
    const usesGroups = getCompanySettings("uses_interchannel_compensation");

    const channelsByGroup = useMemo(() => {
        const groupsById = {};
        filteredChannels.forEach((channel) => {
            const compensationGroupId = channel.compensationGroupId;

            if (compensationGroupId) {
                groupsById[compensationGroupId] = groupsById[compensationGroupId] || [];
                groupsById[compensationGroupId].push(channel);
            }
        });
        return groupsById;
    }, [filteredChannels, channelCompensationGroups]);

    const channelsWithoutGroups = useMemo(() => {
        return paginatedChannels.filter((item) => !item.compensationGroupId);
    }, [paginatedChannels, channelCompensationGroups]);

    const channelsLoading = ["idle", "pending"].includes(channelsStatus);

    return (
        <div className="channels-list">
            {usesGroups &&
                Object.keys(channelsByGroup).map((channelGroup, index) => {
                    const groupInfos = channelCompensationGroups.find((group) => group.id === +channelGroup);

                    return (
                        <div className="channel-compensation-group" key={index}>
                            <span className="group-name">
                                {groupInfos?.name ? (
                                    groupInfos.name
                                ) : (
                                    <span className="group-name other">{t("Channels.OtherChannels")}</span>
                                )}
                            </span>
                            <div className="compensation-group-wrapper">
                                {channelsByGroup[channelGroup].length > 1 && groupInfos?.name && (
                                    <div className="group-line" />
                                )}
                                <div className="channels-group">
                                    {channelsByGroup[channelGroup].map((channel, index) => {
                                        const isChannelOpen = onTargetChannelId === channel.id;

                                        return (
                                            <div
                                                className="channel-wrapper"
                                                style={{ marginTop: "0.25rem" }}
                                                id={`channel-${channel.channelId}`}
                                                key={index}
                                            >
                                                <Channel
                                                    channel={channel}
                                                    onOpenChannel={onOpenChannel}
                                                    isChannelOpen={isChannelOpen}
                                                />
                                                <Collapse in={isChannelOpen} timeout={"auto"}>
                                                    <ChannelDetails
                                                        channel={channel}
                                                        onEditChannel={onEditChannel}
                                                        onDeleteChannel={onDeleteChannel}
                                                        hasEditPermission={hasEditPermission}
                                                    />
                                                </Collapse>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            {/** If no compensation groups */}

            {usesGroups && !channelsLoading && <span className="group-name other">{t("Channels.OtherChannels")}</span>}
            {channelsWithoutGroups?.map((channel, index) => {
                const isChannelOpen = onTargetChannelId === channel.id;

                return (
                    <div className="channel-wrapper" key={index}>
                        <Channel channel={channel} onOpenChannel={onOpenChannel} isChannelOpen={isChannelOpen} />
                        <Collapse in={isChannelOpen} timeout={"auto"}>
                            <ChannelDetails
                                channel={channel}
                                onEditChannel={onEditChannel}
                                onDeleteChannel={onDeleteChannel}
                                hasEditPermission={hasEditPermission}
                            />
                        </Collapse>
                    </div>
                );
            })}
        </div>
    );
};

export default ChannelsList;
